import { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import { useBottom } from '../../../../hooks/useBottom'
import { useMentorAI } from '../../../../context/MentorAIContext'
import { useZoomContext } from '../../../../context/ZoomContext'
import { useContentSharing } from '../../../../context/ContentSharingContext'
import { broadcastIfSharer } from '../../../../utils/mentorAIHelpers'

const MentorAI = ({ selectedText = '' }: { selectedText?: string }) => {
    const { t } = useTranslation('mentor')
    const { messages, addUserMessage, canInteract, selectedContext, setSelectedContext, isAskingMentor, escalated } =
        useMentorAI()
    const { commandChannel, unReadMessages } = useZoomContext()
    const { isContentSharer } = useContentSharing()
    const mainRef = useRef<HTMLElement>(null)
    const textAreaRef = useRef<HTMLTextAreaElement>(null)
    const [newMessage, setNewMessage] = useState(selectedText)
    const [showContext, setShowContext] = useState(false)
    const lastUserQuestionRef = useRef<HTMLDivElement>(null)
    const typingTimeoutRef = useRef<NodeJS.Timeout>()

    // Broadcast typing state
    const broadcastTyping = useCallback(() => {
        if (isContentSharer && commandChannel) {
            // Clear any existing timeout
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current)
            }

            // Broadcast typing state
            broadcastIfSharer(
                {
                    type: 'mentor_ai',
                    action: 'sharer_typing'
                },
                commandChannel,
                isContentSharer
            )

            // Set timeout to clear typing state after 2 seconds of no typing
            typingTimeoutRef.current = setTimeout(() => {
                broadcastIfSharer(
                    {
                        type: 'mentor_ai',
                        action: 'sharer_done'
                    },
                    commandChannel,
                    isContentSharer
                )
            }, 2000)
        }
    }, [isContentSharer, commandChannel])

    // Cleanup typing timeout on unmount
    useEffect(() => {
        return () => {
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current)
            }
        }
    }, [])

    // Effect to broadcast asking state when isAskingMentor changes
    useEffect(() => {
        if (isContentSharer && commandChannel) {
            broadcastIfSharer(
                {
                    type: 'mentor_ai',
                    action: isAskingMentor ? 'sharer_asking' : 'sharer_done'
                },
                commandChannel,
                isContentSharer
            )
        }
    }, [isAskingMentor, isContentSharer, commandChannel])

    // Scroll to the last user question
    const scrollToLastUserQuestion = useCallback(() => {
        if (mainRef.current && lastUserQuestionRef.current) {
            const messageElement = lastUserQuestionRef.current
            const containerElement = mainRef.current
            
            // Calculate the scroll position to show the user question at the top
            const scrollPosition = messageElement.offsetTop - 20 // 20px padding from top
            
            containerElement.scrollTo({
                top: scrollPosition,
                behavior: 'smooth'
            })
        }
    }, [])

    // Scroll to bottom helper with animation option
    const scrollToBottom = useCallback((smooth = true) => {
        if (mainRef.current) {
            const { scrollHeight, clientHeight } = mainRef.current
            mainRef.current.scrollTo({
                top: scrollHeight - clientHeight,
                behavior: smooth ? 'smooth' : 'instant'
            })
        }
    }, [])

    // Initial scroll to bottom on mount (no animation)
    useEffect(() => {
        scrollToBottom(false)
    }, [])

    // Effect to scroll to last user question when mentor responds
    useEffect(() => {
        const latestMessage = messages[messages.length - 1]
        if (latestMessage?.sender === 'mentor' && !isAskingMentor) {
            // Small delay to ensure the DOM has updated
            setTimeout(scrollToLastUserQuestion, 100)
        }
    }, [messages, isAskingMentor, scrollToLastUserQuestion])

    // Focus on textarea when component mounts (only if user can interact)
    useEffect(() => {
        if (canInteract) {
            const timeOutId = setTimeout(() => textAreaRef.current?.focus(), 400)
            return () => clearTimeout(timeOutId)
        }
    }, [canInteract])

    // Focus on textarea when isAskingMentor changes from true to false
    useEffect(() => {
        if (!isAskingMentor && canInteract) {
            const timeOutId = setTimeout(() => textAreaRef.current?.focus(), 100)
            return () => clearTimeout(timeOutId)
        }
    }, [isAskingMentor, canInteract])

    const onSubmit = (e: React.FormEvent<HTMLFormElement | HTMLTextAreaElement>) => {
        e.preventDefault()
        if (!newMessage.trim() || !canInteract) return
        
        // Clear any typing state
        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current)
        }
        
        addUserMessage(newMessage)
        setNewMessage('')
        // Scroll to bottom immediately after submitting (with animation)
        setTimeout(() => scrollToBottom(true), 100)
    }

    // Helper function to find the index of the last user message before the latest mentor message
    const findLastUserQuestionIndex = useCallback((messages: any[]) => {
        for (let i = messages.length - 1; i >= 0; i--) {
            if (messages[i].sender === 'mentor') {
                // Look for the last user message before this mentor message
                for (let j = i - 1; j >= 0; j--) {
                    if (messages[j].sender === 'user') {
                        return j;
                    }
                }
            }
        }
        return -1;
    }, []);

    const hasContext = selectedContext !== undefined

    return (
        <MentorStyled>
            <main ref={mainRef}>
                {/* Show escalation banner if any question has been escalated */}
                {escalated && (
                    <EscalationBanner>
                        <div className="escalation-icon">
                            <FontAwesomeIcon icon={['far', 'user-headset']} />
                        </div>
                        <div className="escalation-content">
                            <div className="escalation-title">{t('escalatedTitle', 'Question Escalated')}</div>
                            <div className="escalation-description">
                                {t(
                                    'escalatedDescription',
                                    'Your question has been escalated to a human expert. You will receive a follow-up soon.'
                                )}
                            </div>
                        </div>
                    </EscalationBanner>
                )}

                {messages && messages.map((message, index) => {
                    // Find the index of the last user question before the latest mentor message
                    const lastUserQuestionIndex = findLastUserQuestionIndex(messages);
                    
                    return (
                        <MessageItem
                            key={message.id}
                            className={`message-item ${message.sender}`}
                            data-sender={message.sender}
                            ref={index === lastUserQuestionIndex ? lastUserQuestionRef : undefined}
                        >
                            <div className="avatar">
                                {message.sender === 'mentor' ? (
                                    <FontAwesomeIcon icon={['far', 'robot']} />
                                ) : (
                                    <FontAwesomeIcon icon={['far', 'user']} />
                                )}
                            </div>
                            <div className="message-content">
                                <div className="message-text">
                                    {message.sender === 'mentor' ? (
                                        <ReactMarkdown>{message.content}</ReactMarkdown>
                                    ) : (
                                        message.content
                                    )}
                                </div>
                                <div className="timestamp">
                                    {new Date(message.timestamp).toLocaleTimeString([], {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                    })}
                                </div>
                            </div>
                        </MessageItem>
                    );
                })}

                {/* Show loading indicator when API request is in progress */}
                {isAskingMentor && (
                    <LoadingIndicator>
                        <div className="avatar">
                            <FontAwesomeIcon icon={['far', 'robot']} />
                        </div>
                        <div className="loading-content">
                            <div className="loading-dots">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                            <div className="loading-text">{t('thinking', 'Thinking...')}</div>
                        </div>
                    </LoadingIndicator>
                )}

                {/* Show sharer typing indicator for viewers */}
                {!isContentSharer && unReadMessages && unReadMessages.sharerTyping && (
                    <LoadingIndicator>
                        <div className="avatar">
                            <FontAwesomeIcon icon={['far', 'user']} />
                        </div>
                        <div className="loading-content">
                            <div className="loading-dots">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                            <div className="loading-text">{t('typing', 'Content sharer is typing...')}</div>
                        </div>
                    </LoadingIndicator>
                )}
                {/* Show sharer asking indicator for viewers */}
                {!isContentSharer && unReadMessages && unReadMessages.sharerAsking && (
                    <LoadingIndicator>
                        <div className="avatar">
                            <FontAwesomeIcon icon={['far', 'user']} />
                        </div>
                        <div className="loading-content">
                            <div className="loading-dots">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                            <div className="loading-text">{t('waiting', 'Waiting for Mentor AI response...')}</div>
                        </div>
                    </LoadingIndicator>
                )}
                {/* Show empty state if no messages */}
                {messages && messages.length === 0 && !isAskingMentor && !unReadMessages && (
                    <EmptyState>
                        <div className="icon">
                            <FontAwesomeIcon icon={['far', 'robot']} size="3x" />
                        </div>
                        <h3>{t('emptyTitle', 'Mentor AI')}</h3>
                        <p>
                            {canInteract
                                ? t('emptyDescription', 'Ask your Mentor AI a question to get started.')
                                : t(
                                      'viewerEmptyDescription',
                                      'Waiting for the content sharer to start a conversation.'
                                  )}
                        </p>
                    </EmptyState>
                )}
            </main>

            {/* Only show input form for users who can interact */}
            {canInteract ? (
                <div>
                    {selectedContext && (
                        <ContextPill onClick={() => setShowContext(!showContext)}>
                            <FontAwesomeIcon icon={['far', 'brain']} />
                            <span>{t('contextAvailable', 'Context added')}</span>
                            <span onClick={() => setSelectedContext(undefined)} style={{ cursor: 'pointer' }}>
                                <FontAwesomeIcon icon={['far', 'times']} />
                            </span>
                        </ContextPill>
                    )}
                    <form className="input-block" onSubmit={onSubmit}>
                        <textarea
                            ref={textAreaRef}
                            placeholder={hasContext ? t('placeholderWithContext') : t('placeholder')}
                            value={newMessage}
                            onChange={({ target: { value } }) => {
                                setNewMessage(value)
                                // Broadcast typing state when content changes
                                if (isContentSharer) {
                                    broadcastTyping()
                                }
                            }}
                            onKeyDown={e => {
                                if (e.key !== 'Enter' || e.shiftKey) return
                                onSubmit(e)
                            }}
                            disabled={isAskingMentor}
                        />
                        <button disabled={!newMessage || isAskingMentor}>
                            {isAskingMentor ? (
                                <FontAwesomeIcon icon={['far', 'spinner']} spin />
                            ) : (
                                <FontAwesomeIcon icon={['far', 'paper-plane-top']} />
                            )}
                        </button>
                    </form>
                </div>
            ) : (
                <NoteBlock>
                    <FontAwesomeIcon icon={['far', 'lock']} />
                    <p>
                        {`${t('viewingSharedConversation', 'This is a shared conversation')}. ${t(
                            'viewerFooter',
                            'Only the content sharer can send messages'
                        )}`}
                        .
                    </p>
                </NoteBlock>
            )}
        </MentorStyled>
    )
}

export default MentorAI

const MentorStyled = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;

    main {
        flex: 1;
        overflow-y: auto;
        scrollbar-width: none; /* For Firefox */
        -ms-overflow-style: none; /* For Internet Explorer and Edge */
        padding: 1rem 0;
        display: flex;
        flex-direction: column;
        gap: 1rem;

        &::-webkit-scrollbar {
            display: none; /* For Chrome, Safari, and Opera */
        }
    }

    .input-block {
        display: flex;
        margin-top: 1rem;

        textarea {
            flex: 1;
            border: 1px solid ${props => props.theme.color.sidebar.gray1};
            border-radius: 8px;
            background-color: ${props => props.theme.color.sidebar.gray2};
            color: white;
            padding: 0.75rem;
            resize: none;
            min-height: 60px;
            font-family: inherit;

            &:focus {
                outline: none;
                border-color: ${props => props.theme.color.sidebar.gray3};
            }

            &:disabled {
                color: rgba(255, 255, 255, 0.7);
                background-color: ${props => props.theme.color.sidebar.gray1};
            }
        }

        button {
            background-color: ${props => props.theme.color.sidebar.gray3};
            color: white;
            border: none;
            border-radius: 8px;
            width: 40px;
            margin-left: 0.5rem;
            cursor: pointer;
            transition: background-color 0.2s;

            &:disabled {
                background-color: rgba(255, 255, 255, 0.1);
                cursor: not-allowed;
            }

            &:hover:not(:disabled) {
                background-color: ${props => props.theme.color.primaryDark};
            }
        }
    }
`

const EscalationBanner = styled.div`
    display: flex;
    gap: 0.75rem;
    padding: 0.75rem;
    margin: 0 0.5rem 1rem;
    background-color: rgba(255, 215, 0, 0.15);
    border: 1px solid rgba(255, 215, 0, 0.3);
    border-radius: 8px;

    .escalation-icon {
        color: #ffd700;
        display: flex;
        align-items: center;
    }

    .escalation-content {
        flex: 1;

        .escalation-title {
            font-weight: 500;
            margin-bottom: 0.25rem;
            color: rgba(255, 255, 255, 0.9);
        }

        .escalation-description {
            font-size: 0.85rem;
            color: rgba(255, 255, 255, 0.7);
            line-height: 1.4;
        }
    }
`

const LoadingIndicator = styled.div`
    display: flex;
    gap: 0.75rem;
    padding: 0 0.5rem;
    text-shadow: none;
    margin-bottom: 1rem;

    .avatar {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background-color: ${props => props.theme.color.sidebar.gray3};
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .loading-content {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;

        .loading-dots {
            display: flex;
            gap: 4px;

            span {
                width: 8px;
                height: 8px;
                border-radius: 50%;
                background-color: rgba(255, 255, 255, 0.5);
                display: inline-block;
                animation: bounce 1.4s infinite ease-in-out both;

                &:nth-child(1) {
                    animation-delay: -0.32s;
                }

                &:nth-child(2) {
                    animation-delay: -0.16s;
                }
            }
        }

        .loading-text {
            font-size: 0.75rem;
            color: rgba(255, 255, 255, 0.5);
        }
    }

    @keyframes bounce {
        0%,
        80%,
        100% {
            transform: scale(0);
        }
        40% {
            transform: scale(1);
        }
    }
`


const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    color: rgba(255, 255, 255, 0.7);
    text-align: center;
    margin: auto 0;

    .icon {
        margin-bottom: 1rem;
        opacity: 0.6;
    }

    h3 {
        margin: 0 0 0.5rem;
        font-size: 1.25rem;
        font-weight: 500;
    }

    p {
        margin: 0;
        font-size: 0.9rem;
        line-height: 1.5;
        color: rgba(255, 255, 255, 0.6);
    }
`

const MessageItem = styled.div`
    display: flex;
    gap: 0.75rem;
    padding: 0 0.5rem;
    text-shadow: none;
    /* Ensure all messages have a reasonable height */
    height: auto;
    min-height: fit-content;

    &.message-item.user {
        flex-direction: row-reverse;
        /* Override any external CSS that might be setting a fixed height */
        height: auto !important;
        max-height: none !important;

        .message-content {
            align-items: flex-end;
            .message-text {
                background-color: ${props => props.theme.color.electricBlue};
                border: 1px solid ${props => props.theme.color.sidebar.gray3};
                border-radius: 12px 12px 0 12px;
            }
        }
    }

    &.message-item.mentor .message-text {
        background-color: ${props => props.theme.color.sidebar.gray1};
        border: 1px solid ${props => props.theme.color.sidebar.gray3};
        border-radius: 12px 12px 12px 0;

        /* Style markdown content */
        p {
            margin: 0 0 1em 0;
            &:last-child {
                margin-bottom: 0;
            }
        }

        code {
            background: rgba(0, 0, 0, 0.2);
            padding: 2px 4px;
            border-radius: 4px;
            font-family: monospace;
        }

        pre {
            background: rgba(0, 0, 0, 0.2);
            padding: 1em;
            border-radius: 4px;
            overflow-x: auto;
            margin: 0.5em 0;

            code {
                background: none;
                padding: 0;
            }
        }

        ul,
        ol {
            margin: 0.5em 0;
            padding-left: 1.5em;
        }

        a {
            color: ${props => props.theme.color.electricBlue};
            text-decoration: none;

            &:hover {
                text-decoration: underline;
            }
        }
    }

    .avatar {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background-color: ${props => props.theme.color.sidebar.gray3};
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .message-content {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        max-width: 90%;

        .message-text {
            padding: 0.75rem;
            color: white;
            word-break: break-word;
        }

        .timestamp {
            font-size: 0.75rem;
            color: rgba(255, 255, 255, 0.5);
        }
    }
`

export const NoteBlock = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 15px;
    padding: 10px;
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 8px;
    margin-top: 1rem;
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.6);
    p {
        font-size: 0.875rem;
        margin: 0;
        line-height: 1.3rem;
    }
`

const ContextPill = styled.div`
    background: ${props => props.theme.color.aiGradient};
    border-radius: 16px;
    padding: 0.25rem 0.75rem;
    margin-bottom: 1rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    transition: all 0.2s ease-in-out;
    border: 1px solid transparent;
    width: fit-content;

    &:hover {
        background-color: ${props => props.theme.color.sidebar.gray3};
        border-color: ${props => props.theme.color.sidebar.gray1};

        svg, span {
            color: white;
        }
    }

    svg {
        width: 12px;
        height: 12px;
        color: white;
        transition: color 0.2s ease-in-out;
    }

    span {
        font-size: 0.875rem;
        color: white;
        transition: color 0.2s ease-in-out;
    }
`
